import apiGet, { apiPost, apiPut, apiDelete } from "../../Api";
import { mediaPath, MEDIA_VARIANTS } from '../../media';

async function readJson(responsePromise) {
  const response = await responsePromise;
  if (!response.ok) return null;
  return response.json();
}

function buildQuery(params = {}) {
  const entries = Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== null && value !== "");
  if (!entries.length) {
    return "";
  }

  const search = new URLSearchParams();
  entries.forEach(([key, value]) => {
    search.append(key, value);
  });

  const query = search.toString();
  return query ? `?${query}` : "";
}

export async function getRegions() {
  return readJson(apiGet("/region"));
}

export async function getEnemies() {
  return readJson(apiGet("/enemy"));
}

export async function getEnemyTypes() {
  return readJson(apiGet("/enemytype"));
}

export async function getItem(itemId) {
  return readJson(apiGet(`/item/${itemId}`));
}

export async function getLevel(levelId) {
  return readJson(apiGet(`/level/${levelId}`));
}

export async function getItems() {
  return readJson(apiGet("/item"));
}

export async function getItemTypes() {
  return readJson(apiGet("/itemtype"));
}

export async function getConsumableEffectTargets() {
  return readJson(apiGet("/item/consumableEffectTargets"));
}

export async function getStats() {
  return readJson(apiGet("/stat"));
}

export async function getRarity() {
  return readJson(apiGet("/rarity"));
}

export async function getLevels() {
  return readJson(apiGet("/Level"));
}

export async function getStarterKits() {
  return readJson(apiGet('/StarterKit'));
}

export async function getStarterKit(starterKitId) {
  if (!starterKitId) return null;
  return readJson(apiGet(`/StarterKit/${encodeURIComponent(starterKitId)}`));
}

export async function getProfessions() {
  return readJson(apiGet('/Profession'));
}

export async function getBaseCampBuildings() {
  return readJson(apiGet('/BaseCampBuilding'));
}

export async function getLootContainerTypes() {
  return readJson(apiGet('/loot-container-types'));
}

async function writeLootContainerType(path, method, value) {
  const response = await method(path, JSON.stringify(value), true);
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(
      payload?.message || 'The loot container type could not be saved.',
    );
    error.issues = payload?.errors || [];
    throw error;
  }
  return payload;
}

export async function createLootContainerType(value) {
  return writeLootContainerType(
    '/loot-container-types',
    apiPost,
    value,
  );
}

export async function updateLootContainerType(id, value) {
  return writeLootContainerType(
    `/loot-container-types/${encodeURIComponent(id)}`,
    apiPut,
    value,
  );
}

export async function createBaseCampBuilding(building) {
  const response = await apiPost(
    '/BaseCampBuilding',
    JSON.stringify(building),
    true,
  );
  return response.json();
}

export async function updateBaseCampBuilding(buildingKey, building) {
  if (!buildingKey) return null;
  const response = await apiPut(
    `/BaseCampBuilding/${encodeURIComponent(buildingKey)}`,
    JSON.stringify(building),
    true,
  );
  return response.json();
}

export async function createProfession(profession) {
  const response = await apiPost('/Profession', JSON.stringify(profession), true);
  return response.json();
}

export async function updateProfession(professionKey, profession) {
  if (!professionKey) return null;
  const response = await apiPut(
    `/Profession/${encodeURIComponent(professionKey)}`,
    JSON.stringify(profession),
    true,
  );
  return response.json();
}

export async function deleteProfession(professionKey) {
  if (!professionKey) return false;
  const response = await apiDelete(
    `/Profession/${encodeURIComponent(professionKey)}`,
    true,
  );
  return response.ok;
}

export async function getProfessionRecipes(professionKey) {
  if (!professionKey) return [];
  return readJson(apiGet(
    `/Profession/${encodeURIComponent(professionKey)}/recipes`,
  ));
}

export async function saveProfessionRecipe(
  professionKey,
  outputItemId,
  recipe,
) {
  if (!professionKey || !outputItemId) return null;
  const response = await apiPut(
    `/Profession/${encodeURIComponent(professionKey)}/recipes/${encodeURIComponent(outputItemId)}`,
    JSON.stringify(recipe),
    true,
  );
  return response.json();
}

export async function deleteProfessionRecipe(professionKey, outputItemId) {
  if (!professionKey || !outputItemId) return false;
  const response = await apiDelete(
    `/Profession/${encodeURIComponent(professionKey)}/recipes/${encodeURIComponent(outputItemId)}`,
    true,
  );
  return response.ok;
}

export async function createStarterKit(starterKit) {
  const response = await apiPost('/StarterKit', JSON.stringify(starterKit), true);
  return response.json();
}

export async function getTileTypes(filters = {}) {
  const query = buildQuery(filters);
  return readJson(apiGet(`/tiletype${query}`));
}

export async function getRegionMap(regionId) {
  if (!regionId) return null;
  return readJson(apiGet(`/regionmap/${regionId}`));
}

export async function saveRegionMap(regionId, payload) {
  if (!regionId) return null;
  const body = JSON.stringify(payload);
  const response = await apiPut(`/regionmap/${regionId}`, body, true);
  return response.json();
}

export async function getSpawnableMapItems() {
  return readJson(apiGet('/regionmap/spawnable-items'));
}

export async function previewRegionResourceSpawns(regionId, regionMap) {
  if (!regionId || !regionMap) return null;
  const response = await apiPost(
    `/regionmap/${encodeURIComponent(regionId)}/resource-spawn-preview`,
    JSON.stringify(regionMap),
    true,
  );
  return response.json();
}

export async function previewRegionLootContainerSpawns(
  regionId,
  regionMap,
) {
  if (!regionId || !regionMap) return null;
  const response = await apiPost(
    `/regionmap/${encodeURIComponent(regionId)}/loot-container-spawn-preview`,
    JSON.stringify(regionMap),
    true,
  );
  return response.json();
}

export async function previewRegionEnemySpawns(regionId, regionMap) {
  if (!regionId || !regionMap) return null;
  const response = await apiPost(
    `/enemy/region/${encodeURIComponent(regionId)}/spawn-preview`,
    JSON.stringify(regionMap),
    true,
  );
  return response.json();
}

export async function getTileDecorations(filters = {}) {
  return getTileTypes({ ...filters, type: 'detail' });
}

export async function getChangelogEntries() {
  return readJson(apiGet('/changelog/admin'));
}

export async function getChangelogStatus() {
  return readJson(apiGet('/changelog/admin/status'));
}

export async function setChangelogVisibility(id, visible) {
  const response = await apiPut(`/changelog/${id}/visibility`, JSON.stringify({ visible }), true);
  if (!response.ok) throw new Error('Could not update changelog visibility.');
  return response;
}

export async function createChangelogEntry(entry) {
  const response = await apiPost('/changelog', JSON.stringify(entry), true);
  if (!response.ok) throw new Error('Could not create changelog entry.');
  return response.json();
}

export async function updateChangelogEntry(id, entry) {
  const response = await apiPut(`/changelog/${id}`, JSON.stringify(entry), true);
  if (!response.ok) throw new Error('Could not update changelog entry.');
  return response.json();
}

export async function deleteChangelogEntry(id) {
  const response = await apiDelete(`/changelog/${id}`, true);
  if (!response.ok) throw new Error('Could not delete changelog entry.');
  return response;
}

export async function getAdminUsers({
  search = '',
  offset = 0,
  pageSize = 25,
} = {}) {
  return readJson(apiGet(`/admin/users${buildQuery({
    search,
    offset,
    pageSize,
  })}`));
}

export async function updateAdminUser(id, account) {
  const response = await apiPut(
    `/admin/users/${encodeURIComponent(id)}`,
    JSON.stringify(account),
    true,
  );
  return response.json();
}

export async function updateAdminUserPassword(id, newPassword) {
  await apiPut(
    `/admin/users/${encodeURIComponent(id)}/password`,
    JSON.stringify({ newPassword }),
    true,
  );
}

export async function grantAdminUserItem(
  id,
  itemId,
  quantity,
  rarityId = '',
) {
  const response = await apiPost(
    `/admin/users/${encodeURIComponent(id)}/inventory`,
    JSON.stringify({ itemId, quantity, rarityId }),
    true,
  );
  return response.json();
}

export async function getAdminUserInventory(id) {
  return readJson(apiGet(
    `/admin/users/${encodeURIComponent(id)}/inventory`,
  ));
}

export async function updateAdminUserInventoryItem(
  id,
  x,
  y,
  quantity,
) {
  const response = await apiPut(
    `/admin/users/${encodeURIComponent(id)}/inventory/${x}/${y}`,
    JSON.stringify({ quantity }),
    true,
  );
  return response.json();
}

export async function importChangelog() {
  return readJson(apiPost('/changelog/import', null, true));
}

export async function getWikiPages() {
  return readJson(apiGet('/WikiPage'));
}

export async function createWikiPage(page) {
  return readJson(apiPost('/WikiPage', JSON.stringify(page), true));
}

export async function updateWikiPage(id, page) {
  return readJson(apiPut(`/WikiPage/${encodeURIComponent(id)}`, JSON.stringify(page), true));
}

export async function deleteWikiPage(id) {
  const response = await apiDelete(`/WikiPage/${encodeURIComponent(id)}`, true);
  if (!response.ok) throw new Error('Could not delete wiki page.');
  return response;
}

export async function getContentPromotionStatus() {
  return readJson(apiGet('/content-promotion/status'));
}

export async function getContentExportOptions() {
  return readJson(apiGet('/content-promotion/export-options'));
}

export async function exportContentPackage(selection = null) {
  const response = selection === null
    ? await apiGet('/content-promotion/export')
    : await apiPost(
      '/content-promotion/export',
      JSON.stringify({ records: selection }),
      true,
    );
  const blob = await response.blob();
  const digest = response.headers.get('x-content-digest') || '';
  const disposition = response.headers.get('content-disposition') || '';
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
  return {
    blob,
    digest,
    filename: filenameMatch?.[1] || `progresslands-${digest.slice(0, 12) || 'content'}.plcontent`,
    recordCount: Number(response.headers.get('x-content-record-count') || 0),
    mediaCount: Number(response.headers.get('x-content-media-count') || 0),
    selectedRecordCount: Number(response.headers.get('x-content-selected-count') || 0),
    dependencyRecordCount: Number(response.headers.get('x-content-dependency-count') || 0),
  };
}

export async function previewContentPackage(file) {
  const formData = new FormData();
  formData.append('package', file);
  const response = await apiPost('/content-promotion/preview', formData, true);
  return response.json();
}

export async function applyContentPackage({
  file,
  previewToken,
  confirmationText,
  ptsVerified,
}) {
  const formData = new FormData();
  formData.append('package', file);
  formData.append('previewToken', previewToken);
  formData.append('confirmationText', confirmationText);
  formData.append('ptsVerified', String(Boolean(ptsVerified)));
  const response = await apiPost('/content-promotion/apply', formData, true);
  return response.json();
}

export async function getContentPromotionRuns(limit = 25) {
  return readJson(apiGet(`/content-promotion/runs?limit=${encodeURIComponent(limit)}`));
}

export async function createTile(tile) {
  const response = await apiPost('/tiletype', JSON.stringify(tile), true);
  if (!response.ok) {
    return null;
  }
  return response.json();
}

export async function updateTile(tileId, tile) {
  if (!tileId) return null;
  const response = await apiPut(`/tiletype/${tileId}`, JSON.stringify(tile), true);
  return response.ok;
}

export async function deleteTile(tileId) {
  if (!tileId) return null;
  const response = await apiDelete(`/tiletype/${tileId}`, true);
  return response.ok;
}

export async function getAvatarAdminConfig() {
  return readJson(apiGet('/avatar/admin/config'));
}

export async function getAvatarOverview() {
  return readJson(apiGet('/avatar/admin/overview'));
}

export async function saveAvatarLayerRequirement(slotKey, required) {
  return readJson(apiPut(
    `/avatar/admin/layer-slots/${encodeURIComponent(slotKey)}/requirement`,
    JSON.stringify({ required }),
    true,
  ));
}

export async function saveAvatarPart(part) {
  const path = part?.id ? `/avatar/admin/parts/${encodeURIComponent(part.id)}` : '/avatar/admin/parts';
  const request = part?.id ? apiPut(path, JSON.stringify(part), true) : apiPost(path, JSON.stringify(part), true);
  return readJson(request);
}

export async function publishAvatarPart(partId) {
  return readJson(apiPost(`/avatar/admin/parts/${encodeURIComponent(partId)}/publish`, null, true));
}

export async function retireAvatarPart(partId) {
  return readJson(apiPost(`/avatar/admin/parts/${encodeURIComponent(partId)}/retire`, null, true));
}

export async function reopenAvatarPart(partId) {
  return readJson(apiPost(`/avatar/admin/parts/${encodeURIComponent(partId)}/reopen`, null, true));
}

export async function reactivateAvatarPart(partId) {
  return readJson(apiPost(`/avatar/admin/parts/${encodeURIComponent(partId)}/reactivate`, null, true));
}

export async function updateRegion(region) {
  if (!region || !region.id) return null;
  return apiPut('/region', JSON.stringify(region), true);
}

export async function fetchImage(imageId, variant = MEDIA_VARIANTS.PICTURE) {
  try {
    const response = await apiGet(mediaPath(imageId, variant));
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching image:', error);
    return null;
  }
}

export async function handleUpload(file) {
  const formData = new FormData();
  formData.append('files', file);

  try {
    const response = await apiPost('/Image', formData, true);
    if (!response.ok) {
      console.error('Error uploading file:', await response.text());
      return null;
    }

    const payload = await response.json();
    return payload.id;
  } catch (error) {
    console.error('Network error:', error);
    return null;
  }
}

export async function handleMediaUpload(file, profile) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('profile', profile);

  try {
    const response = await apiPost('/media', formData, true);
    if (!response.ok) return null;
    const payload = await response.json();
    return payload.id || payload.assetId || payload.mediaId || null;
  } catch (error) {
    console.error('Error uploading media:', error);
    return null;
  }
}

const AdminAPI = {
  getRegions,
  getEnemies,
  getEnemyTypes,
  getItem,
  getLevel,
  getItems,
  getItemTypes,
  getConsumableEffectTargets,
  getStats,
  getRarity,
  getLevels,
  getStarterKits,
  getStarterKit,
  getProfessions,
  getBaseCampBuildings,
  getLootContainerTypes,
  createLootContainerType,
  updateLootContainerType,
  createBaseCampBuilding,
  updateBaseCampBuilding,
  createProfession,
  updateProfession,
  deleteProfession,
  getProfessionRecipes,
  saveProfessionRecipe,
  deleteProfessionRecipe,
  createStarterKit,
  getTileTypes,
  getRegionMap,
  saveRegionMap,
  getSpawnableMapItems,
  previewRegionResourceSpawns,
  previewRegionLootContainerSpawns,
  previewRegionEnemySpawns,
  getTileDecorations,
  getChangelogEntries,
  getChangelogStatus,
  setChangelogVisibility,
  createChangelogEntry,
  updateChangelogEntry,
  deleteChangelogEntry,
  getAdminUsers,
  updateAdminUser,
  updateAdminUserPassword,
  grantAdminUserItem,
  getAdminUserInventory,
  updateAdminUserInventoryItem,
  importChangelog,
  getContentPromotionStatus,
  getContentExportOptions,
  exportContentPackage,
  previewContentPackage,
  applyContentPackage,
  getContentPromotionRuns,
  createTile,
  updateTile,
  deleteTile,
  updateRegion,
  fetchImage,
  handleUpload,
  handleMediaUpload,
  getAvatarAdminConfig,
  getAvatarOverview,
  saveAvatarLayerRequirement,
  saveAvatarPart,
  publishAvatarPart,
  retireAvatarPart,
};

export default AdminAPI;
